/*
    useAuthentication
    ---
    Provides authentication features using Firebase Auth.

*/

import { ref, computed } from 'vue';
import {
    signInWithCustomToken,
    signInWithPopup as signInFirebase,
    signOut as signOutFirebase,
    signInWithEmailAndPassword,
    signInWithEmailLink,
    isSignInWithEmailLink,
    verifyPasswordResetCode,
    confirmPasswordReset,
    fetchSignInMethodsForEmail,
    GoogleAuthProvider,
    FacebookAuthProvider,
    OAuthProvider,
    browserPopupRedirectResolver,
    onAuthStateChanged,
    getAdditionalUserInfo,
    applyActionCode,
} from 'firebase/auth';
import { useSessionApi } from './useSessionApi';
import { useInvitesApi } from './useInvitesApi';
import { useProduct } from './useProduct';
import { UserProvider } from 'ihunter-common';
import { useFirebase } from './useFirebase';
import { useEnvironment, isLocalEnv } from './useEnvironment';
import { useAnalytics } from './useAnalytics';
import { AuthType } from './useConstants';

let userProvider, initialized, sessionUserUid;

const user = ref(undefined); // Global user-state
const embeddedResetPasswordUrl = ref(undefined);
const embeddedVerifyEmailUrl = ref(undefined);
const verifyEmailActionCode = ref(undefined);

export function initializeAuthUrls() {
    const setEmbeddedResetPasswordUrl = (encodedValue) => {
        try {
            embeddedResetPasswordUrl.value = atob(encodedValue);
            console.log(`embeddedResetPasswordUrl`, embeddedResetPasswordUrl.value);
        } catch (error) {
            console.warn(`embeddedResetPasswordUrl`, error);
        }
    };

    const setEmbeddedVerifyEmailUrl = (encodedValue) => {
        try {
            embeddedVerifyEmailUrl.value = atob(encodedValue);
            console.log(`embeddedVerifyEmailUrl`, embeddedVerifyEmailUrl.value);
            const urlParams = new URLSearchParams(embeddedVerifyEmailUrl.value);
            verifyEmailActionCode.value = urlParams.get('oobCode');
            console.log(`verifyEmailActionCode=${verifyEmailActionCode.value}`);
        } catch (error) {
            console.warn(`setEmbeddedVerifyEmailUrl`, error);
        }
    };
    return { setEmbeddedResetPasswordUrl, setEmbeddedVerifyEmailUrl };
}

export const useAuthentication = () => {
    const { firebaseApp, auth } = useFirebase();
    const { config } = useEnvironment();
    const { loginToSession, getCustomTokenForExistingSession, logoutOfSession, sendSignInLinkToEmail, sendResetPasswordLinkToEmail } =
        useSessionApi();
    const { callResendInviteApi } = useInvitesApi();
    const { inviteUid, hasSignInLink, continueToProduct, continueToAccounts } = useProduct();
    const { setAnalyticsUser, logAuthAccountCreated, logAuthLinkRequested, logAuthPasswordReset, logAuthSignedIn, logAuthSignedOut } = useAnalytics();

    if (!userProvider) {
        console.log(`useAuthentication - initializing for env=${config.value.name}, project=${firebaseApp?.options?.projectId}`);
        userProvider = new UserProvider(firebaseApp);
    }

    let uid = computed(() => user.value?.uid);

    onAuthStateChanged(auth, async (firebaseUser) => {
        setAnalyticsUser(firebaseUser);
        if (initialized || user.value?.isTestUser) {
            return;
        }
        initialized = true;

        if (!firebaseUser) {
            try {
                await signInWithExistingSessionInternal();
            } catch (error) {
                if (!(await tryBypassAuthForLocalEnv())) {
                    clearUserSession();
                }
            }
        } else {
            if (firebaseUser.needsPasswordReset === undefined) {
                firebaseUser.needsPasswordReset = await userProvider.getNeedsPasswordReset(firebaseUser.uid);
            }
            user.value = firebaseUser;
            console.info(
                `[onAuthStateChanged] User ${user.value.uid} initialized, isNewUser=${user.value.isNewUser}, needsPasswordReset=${user.value.needsPasswordReset}`,
                firebaseUser,
            );
        }
    });

    /**
     * When running locally you can bypass Session API auth & avoid being redirected to Accounts
     * by providing your email and password credentials as url params.
     *
     * This is useful for local Safari testing which enforces same-origin policy more rigorously than Chrome,
     * and does not permit calls to the http session api.
     *
     * @returns (boolean) true if sign in was successful with the provided credentials, and false otherwise.
     */
    const tryBypassAuthForLocalEnv = async () => {
        try {
            if (isLocalEnv.value) {
                const params = new URLSearchParams(window.location.search);
                const email = params.get('email');
                const password = params.get('password');

                if (email && password) {
                    const credential = await signInWithEmailAndPassword(auth, email, password);
                    console.log(`Successfully bypassed auth for local env for ${email}: uid=${credential.user.uid}`);
                    setUserSessionValid(credential.user);

                    // Remove email and password from URL without reloading
                    params.delete('email');
                    params.delete('password');
                    const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
                    window.history.replaceState({}, '', newUrl);

                    return true;
                }
            }
        } catch (err) {
            console.error(`Failed to bypassed auth for local env`, err);
        }
        return false;
    };

    const isUserSessionValid = () => {
        return user.value && sessionUserUid && sessionUserUid === user.value.uid;
    };

    const setUserSessionValid = (firebaseUser) => {
        if (firebaseUser.uid !== user.value?.uid) {
            console.info(`User ${firebaseUser.uid} has a valid session`);
            user.value = firebaseUser;
            sessionUserUid = user.value.uid;
        }
    };

    const clearUserSession = () => {
        console.info(`User session cleared`);
        user.value = null;
        sessionUserUid = null;
    };

    const signInWithExistingSession = async () => {
        if (isUserSessionValid()) {
            console.info(`signInWithExistingSession - session already validated for user ${sessionUserUid}`);
            return;
        }

        try {
            await signInWithExistingSessionInternal();
        } catch (error) {
            try {
                console.error(`signInWithExistingSession failed, clearing user and session`, error);
                if (auth.currentUser) {
                    await signOut();
                } else {
                    clearUserSession();
                }
            } catch (e) {}
            throw error;
        }
    };

    const signInWithExistingSessionInternal = async () => {
        const response = await getCustomTokenForExistingSession();
        const credentials = await signInWithCustomToken(auth, response.access_token);
        setUserSessionValid(credentials.user);
    };

    const signInToNewSession = async (user) => {
        try {
            console.log(`signInToNewSession for user ${user.uid}...`);
            await loginToSession(user);
            setUserSessionValid(user);
            await checkIfNeedsPasswordReset();
            return user;
        } catch (error) {
            try {
                await signOut();
            } catch (signOutError) {}
            throw error;
        }
    };

    const emailSignInExists = async (email) => {
        let signInMethods = await fetchSignInMethodsForEmail(auth, email);
        if (signInMethods.length > 0 && !signInMethods.includes('password')) {
            const error = new Error();
            error.code = 'auth/account-exists-with-different-credential';
            error.providers = signInMethods;
            throw error;
        }
        return signInMethods.length > 0;
    };

    const signInWithEmail = async (email, password) => {
        try {
            console.log(`signInWithEmail`);
            logAuthSignedIn(AuthType.Password);
            const credential = await signInWithEmailAndPassword(auth, email, password);
            credential.user.isNewUser = false;
            return await signInToNewSession(credential.user);
        } catch (error) {
            if (error.code === 'auth/account-exists-with-different-credential') {
                error.providers = await fetchSignInMethodsForEmail(auth, error?.customData?.email);
            }
            throw error;
        }
    };

    const signInWithProvider = async (provider) => {
        let authProvider = null,
            authType = null;

        switch (provider) {
            case 'facebook.com':
                authProvider = new FacebookAuthProvider();
                authType = AuthType.FacebookProvider;
                break;

            case 'google.com':
                authProvider = new GoogleAuthProvider();
                authProvider.setCustomParameters({ prompt: 'select_account' });
                authType = AuthType.GoogleProvider;
                break;

            case 'apple.com':
            default:
                authProvider = new OAuthProvider(provider);
                authType = AuthType.AppleProvider;
                break;
        }

        try {
            let credential = await signInFirebase(auth, authProvider, browserPopupRedirectResolver);
            updateIsNewUserForCredential(credential);
            await createNewUserInDb(credential.user, null, authType);
            await signInToNewSession(credential.user);
            logAuthSignedIn(authType);
        } catch (error) {
            if (error.code === 'auth/account-exists-with-different-credential') {
                const credential = credentialFromError(provider, error);
                error.providers = await fetchSignInMethodsForEmail(auth, error?.customData?.email);
                error.credential = credential;
            }
            throw error;
        }
    };

    const updateIsNewUserForCredential = (credential) => {
        // https://firebase.google.com/docs/reference/js/auth.additionaluserinfo
        const additionalInfo = getAdditionalUserInfo(credential);
        credential.user.isNewUser = additionalInfo?.isNewUser;
        console.log(`getAdditionalUserInfo: isNewUser=${additionalInfo?.isNewUser}`);
    };

    const createNewUserInDb = async (user, password, authType) => {
        try {
            if (user.isNewUser) {
                console.info(`createNewUserInDb for user ${user.uid}`);
                await userProvider.createNewUser(user, password);
                logAuthAccountCreated(authType);
            }
        } catch (error) {
            console.error(`createNewUserInDb`, error);
        }
    };

    const credentialFromError = (provider, error) => {
        switch (provider) {
            case 'facebook.com':
                return FacebookAuthProvider.credentialFromError(error);

            case 'google.com':
                return GoogleAuthProvider.credentialFromError(error);

            case 'apple.com':
            default:
                return OAuthProvider.credentialFromError(error);
        }
    };

    const signOut = async () => {
        try {
            console.info(`Signing out and clearing session cookie for user ${user.value?.uid}...`);
            if (user.value) {
                logAuthSignedOut();
                await signOutFirebase(auth);
            }
            await logoutOfSession();
            clearUserSession();
            console.info('Sign out complete');
        } catch (error) {
            console.error('Sign out', error);
            clearUserSession();
            throw error;
        }
    };

    const hasPasswordProvider = () => {
        try {
            if (user.value && user.value.providerData) {
                const hasProvider = Boolean(
                    user.value.providerData.find((x) => {
                        return x.providerId === 'password';
                    }),
                );
                console.info(`hasPasswordProvider ${hasProvider}`, user.value.providerData);
                return hasProvider;
            }
            return false;
        } catch (error) {
            console.error('hasPasswordProvider', error);
        }
        return false;
    };

    const sendResetPasswordLink = async (email) => {
        try {
            if (!email) {
                throw new Error('Must provide an email to send the reset password link to');
            }
            await sendResetPasswordLinkToEmail(email, continueToProduct.value, continueToAccounts.value);
            logAuthLinkRequested(AuthType.ResetPassword);
        } catch (error) {
            console.error('Send reset password email', error);
            throw error;
        }
    };

    const validateResetPasswordCode = async (actionCode) => {
        try {
            return await verifyPasswordResetCode(auth, actionCode);
        } catch (error) {
            console.error('Verify reset password code', error);
            throw error;
        }
    };

    const resetPassword = async (actionCode, newPassword) => {
        try {
            const uid = user.value?.uid;
            await confirmPasswordReset(auth, actionCode, newPassword);
            if (uid) {
                console.log(`resetPassword success - updating password status`);
                userProvider.updatePasswordStatus(uid, Date.now(), false);
            } else {
                console.log(`resetPassword success - user not authenticated, not updating password status`);
            }
            logAuthPasswordReset();
        } catch (error) {
            console.error('Reset password', error);
            throw error;
        }
    };

    const sendSignInLink = async (email) => {
        try {
            if (!email) {
                throw new Error('Must provide an email to send the sign in link to');
            }
            await sendSignInLinkToEmail(email, continueToProduct.value);
            logAuthLinkRequested(AuthType.SignInLink);
        } catch (error) {
            console.error('Send sign-in link email', error);
            throw error;
        }
    };

    const resendAcceptInviteLink = async (email) => {
        try {
            if (!email) {
                throw new Error('Must provide an email to resend the invite to');
            }
            await callResendInviteApi(email, inviteUid.value);
            logAuthLinkRequested(AuthType.InviteLink);
        } catch (error) {
            console.error('Resend accept invite link email', error);
            throw error;
        }
    };

    const signInWithAcceptInviteLink = async (email) => {
        try {
            if (!email) {
                throw new Error('Must provide an email to accept the invite link');
            }
            if (validateSignInLink()) {
                const autoVerifiedEmail = await applyVerifyEmailActionCode();
                let credential = await signInWithEmailLink(auth, email, window.location.href);
                updateIsNewUserForCredential(credential);
                console.log(`User ${credential.user.uid} successfully signed in with accept invite link`);
                await updateDbWithEmailLinkSignIn(credential.user.uid, autoVerifiedEmail);
                if (!credential.user.isNewUser) {
                    await signInToNewSession(credential.user);
                }
                logAuthSignedIn(AuthType.InviteLink);
                return credential.user;
            }
        } catch (error) {
            console.error('signInWithAcceptInviteLink', error);
        }
    };

    const createUserAndSignInToSession = async (newUser, displayName, password) => {
        try {
            newUser.displayName = displayName;
            await createNewUserInDb(newUser, password, AuthType.Password);
            await signInToNewSession(newUser);
        } catch (error) {
            console.error('createUserAndSignInToSession', error);
        }
    };

    const signInWithLink = async (email, displayName, password) => {
        try {
            const autoVerifiedEmail = await applyVerifyEmailActionCode();
            let credential = await signInWithEmailLink(auth, email, window.location.href);
            await updateDbWithEmailLinkSignIn(credential.user.uid, autoVerifiedEmail);
            updateIsNewUserForCredential(credential);
            if (credential.user.isNewUser) {
                await createUserAndSignInToSession(credential.user, displayName, password);
            } else {
                await signInToNewSession(credential.user);
            }
            logAuthSignedIn(AuthType.Passwordless);
        } catch (error) {
            console.error('Sign-in with link', error);
            throw error;
        }
    };

    const validateSignInLink = () => {
        try {
            return hasSignInLink.value && isSignInWithEmailLink(auth, window.location.href);
        } catch (error) {
            console.error('Verify sign-in link', error);
            throw error;
        }
    };

    const applyVerifyEmailActionCode = async () => {
        try {
            if (verifyEmailActionCode.value) {
                console.log(`Applying action code to verify the email before passwordless sign in`, verifyEmailActionCode.value);
                await applyActionCode(auth, verifyEmailActionCode.value);
                console.log(`applyEmailActionCode success!`);
                return true;
            } else {
                console.log(`No verify email action code provided`);
            }
        } catch (error) {
            console.error('applyEmailActionCode', error);
        }
        return false;
    };

    const updateDbWithEmailLinkSignIn = async (uid, autoVerifiedEmail) => {
        await userProvider.updateEmailLinkSignInTimestamp(uid);
        if (autoVerifiedEmail) {
            await userProvider.updateEmailAutoVerifiedTimestamp(uid);
        }
    };

    const checkIfNeedsPasswordReset = async () => {
        try {
            if (user.value) {
                const hasPassword = hasPasswordProvider();
                const passwordUpdatedAt = user.value.reloadUserInfo?.passwordUpdatedAt;
                if (hasPassword) {
                    const createdAt = parseInt(user.value.metadata?.createdAt);
                    // needsPasswordReset is true if:
                    // 1. The password has never been updated (account creation time matches the password update time), AND
                    // 2. The email is not verified OR the BE embedded the reset password url in the sign in link (meaning the email was not verified prior to passwordless sign in)
                    user.value.needsPasswordReset = Boolean(
                        createdAt === passwordUpdatedAt && (!user.value.emailVerified || embeddedResetPasswordUrl.value),
                    );
                    console.log(
                        `checkIfNeedsPasswordReset: needsPasswordReset=${user.value.needsPasswordReset}, createdAt=${createdAt}, passwordUpdatedAt=${passwordUpdatedAt}, emailVerified=${user.value.emailVerified}`,
                    );
                } else {
                    console.log(
                        `checkIfNeedsPasswordReset: skipping for current user: isNewUser=${user.value.isNewUser}, passwordUpdatedAt=${passwordUpdatedAt}, emailVerified=${user.value.emailVerified}, hasPassword=${hasPassword}`,
                    );
                }

                // Store passwordUpdatedAt for troubleshooting as well as BE access, since it is not available using Firebase auth admin
                await userProvider.updatePasswordStatus(user.value.uid, passwordUpdatedAt, user.value.needsPasswordReset);
            }
        } catch (error) {
            console.error('checkIfNeedsPasswordReset', error);
        }
        return false;
    };

    return {
        user,
        uid,
        embeddedResetPasswordUrl,
        emailSignInExists,
        signInWithExistingSession,
        signInWithProvider,
        signInWithEmail,
        signInWithLink,
        sendSignInLink,
        validateSignInLink,
        createUserAndSignInToSession,
        signInWithAcceptInviteLink,
        resendAcceptInviteLink,
        signOut,
        sendResetPasswordLink,
        validateResetPasswordCode,
        resetPassword,
        hasPasswordProvider,
    };
};
